import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { AbstractControl, NgModel } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatDatepicker } from '@angular/material/datepicker';
import { PageEvent } from '@angular/material/paginator';
import { MatDrawer } from '@angular/material/sidenav';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ValidationRegexPatterns } from '@xpo-ltl/common-services';
import { ListInfo, PayformStatusCd, SortField } from '@xpo-ltl/sdk-common';
import { DsrEmployee, HumanResourceApiService, SearchEmployeeDetailsBySicResp, SearchEmployeeDetailsBySicRqst } from '@xpo-ltl/sdk-humanresource';
import { LinehaulPayformApiService, ListLinehaulPayformsRqst, ListLinehaulPayformsQuery } from '@xpo-ltl/sdk-linehaulpayform';
import * as _ from 'lodash';
import { Moment } from 'moment';
import * as moment from 'moment-timezone';
import { Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, skipWhile, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { ListPayformsCell } from '../../classes/list-payforms-cell';
import { AccessLevel } from '../../enums/access-level.enum';
import { PayformSortFields } from '../../enums/payform-sort-fields.enum';
import { RouterUriComponents } from '../../enums/router-uri-components.enum';
import { SubstringPipe } from '../../pipes/substring-pipe';
import { AppConstantsService } from '../../services/app-constants.service';
import { AppFooterService } from '../../services/app-footer/app-footer.service';
import { DriverPayService } from '../../services/driver-pay/driver-pay.service';
import { ConfigManagerProperties, PaginatorEventType } from '../../enums';
import { ConfigManagerService } from '@xpo-ltl/config-manager';
import { Store } from '@ngrx/store';
import { payformsFilterStateSelector, setPayformsFilterStateAction } from '@app/state/payforms-filter';
import { PayformsFilterState } from '@app/state';
import { PayformsFilter } from 'app/classes';
import { Unsubscriber } from 'app/utils/angular-utils/classes';
import { PaginatorComponent } from 'app/utils/angular-utils/components/paginator/paginator.component';

@Component({
  selector: 'app-list-payforms',
  templateUrl: './list-payforms.component.html',
  styleUrls: ['./list-payforms.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ListPayformsComponent implements OnInit, OnDestroy {
  public displayedColumns: string[] = ['empName', 'empId', 'sic', 'shiftStartDate', 'status'];
  public dataSource = new MatTableDataSource<ListPayformsCell>();
  public payformStatusCode = PayformStatusCd;
  public statusAll = false;
  public statusSubmitted = false;
  public statusApproved = false;
  public statusReturned = false;
  public statusNotSubmitted = false;
  public statusDeleted = false;
  public sic;
  public employeeName: string;
  public dateFrom: Date;
  public dateTo: Date;

  private employeeListSubject = new Subject<DsrEmployee[]>();
  public employeeList$ = this.employeeListSubject.asObservable();
  public searching = false;

  public readonly RouterUriComponents = RouterUriComponents;
  public readonly AccessLevel = AccessLevel;
  public readonly ValidationRegexPatterns = ValidationRegexPatterns;

  private isDrawerOpen = false;
  private currentFilterSic: string;
  private currentFilterEmployee: string;
  private currentFilterFrom: Date;
  private currentFilterTo: Date;
  private currentFilterAllChecked: boolean;
  private currentFilterSubmittedChecked: boolean;
  private currentFilterApprovedChecked: boolean;
  private currentFilterReturnedChecked: boolean;
  private currentFilterNotSubmittedChecked: boolean;
  private currentFilterDeletedChecked: boolean;

  @ViewChild(MatSort)
  sort: MatSort;
  @ViewChild(PaginatorComponent)
  paginator: PaginatorComponent;

  @ViewChild(MatDrawer)
  drawer: MatDrawer;
  @ViewChild('sicInput', { static: true })
  sicInput: AbstractControl;
  @ViewChild('pickerFrom', { static: true })
  pickerFrom: MatDatepicker<Moment>;
  @ViewChild('dateFromInput', { static: true })
  dateFromInput: NgModel;
  @ViewChild('pickerTo', { static: true })
  pickerTo: MatDatepicker<Moment>;
  @ViewChild('dateToInput', { static: true })
  dateToInput: NgModel;
  @ViewChild('employeeNameInput', { static: true })
  employeeNameInput: NgModel;

  filters$: Observable<PayformsFilterState>;

  pageEvent: PageEvent;
  currentSort: SortField[] = [];
  dataCache = {};

  private unsubscriber = new Unsubscriber();
  private lastFilter: PayformsFilterState;

  private includeZones = true;

  constructor(
    private linehaulService: LinehaulPayformApiService,
    private datePipe: DatePipe,
    public driverPayService: DriverPayService,
    public constants: AppConstantsService,
    private hrService: HumanResourceApiService,
    private footerService: AppFooterService,
    private substringPipe: SubstringPipe,
    private config: ConfigManagerService,
    private store: Store
  ) {
    this.includeZones = this.config.getSetting<boolean>(ConfigManagerProperties.includeZones);
  }

  ngOnInit() {
    this.footerService.clearLeftButtonConfig();
    this.footerService.clearRightButtonConfig();

    // subscribe to changes in the AppState filters
    this.filters$.pipe(takeUntil(this.unsubscriber.done)).subscribe((filter: PayformsFilterState) => {
      if (!this.sic || this.sic.toUpperCase() !== filter.sicCd.toUpperCase()) {
        this.sic = filter.sicCd;
      }
      this.employeeName = filter.employeeName;
      this.dateFrom = filter.fromDate ? filter.fromDate.toDate() : undefined;
      this.dateTo = filter.toDate ? filter.toDate.toDate() : undefined;
      this.updateStatus(filter);

      this.dateFromInput.valueChanges.pipe(takeUntil(this.unsubscriber.done)).subscribe(() => {
        this.dateFromInput.control.updateValueAndValidity();
        this.dateToInput.control.updateValueAndValidity();
      });
      this.dateToInput.valueChanges.pipe(takeUntil(this.unsubscriber.done)).subscribe(() => {
        this.dateFromInput.control.updateValueAndValidity();
        this.dateToInput.control.updateValueAndValidity();
      });

      if (!this.lastFilter || !_.isEqual(this.lastFilter, filter)) {
        this.dataCache = {};

        this.loadPayForms(filter);
        this.lastFilter = _.cloneDeep(filter);
        this.paginator.pageIndex = 0;
        this.store.dispatch(setPayformsFilterStateAction({ filter }));
      }
    });

    const empInputErrors = () => ({ 'not-found': 'Employee not found.' });

    this.employeeNameInput.valueChanges
      .pipe(
        takeUntil(this.unsubscriber.done),
        distinctUntilChanged(),
        tap(value => {
          this.employeeListSubject.next(undefined);

          this.searching = value && value.length > 0;

          return value;
        }),
        debounceTime(100),
        switchMap(value => this.lookupEmployee(this.substringPipe.transform(value, ['(', ')']))),
        skipWhile(value => !value)
      )
      .subscribe(
        response => {
          this.searching = false;

          if (response && response.dsrEmployees) {
            this.employeeListSubject.next(_.sortBy(response.dsrEmployees, (emp: DsrEmployee) => `${emp.lastName}, ${emp.firstName}`));

            const empId = this.substringPipe.transform(this.employeeName, ['(', ')']);

            const dsrEmployee = response.dsrEmployees.find(emp => emp.employeeId === empId.toUpperCase());
            if (dsrEmployee) {
              this.employeeNameInput.control.setValue(`${dsrEmployee.fullName} (${dsrEmployee.employeeId})`);
            }
            this.employeeNameInput.control.setErrors(dsrEmployee ? undefined : empInputErrors());
          }
        },
        errors => {
          this.searching = false;
          this.employeeNameInput.control.setErrors(undefined);
        }
      );

    this.drawer.openedChange.pipe(takeUntil(this.unsubscriber.done)).subscribe(openChanged => {
      this.isDrawerOpen = openChanged;

      if (this.isDrawerOpen) {
        this.saveCurrentFilterState();
      }
    });

    this.paginator.page.pipe(takeUntil(this.unsubscriber.done), distinctUntilChanged()).subscribe(pageEvent => {
      if (pageEvent.eventType === PaginatorEventType.UpdatePgSize) {
        this.dataCache = {};
      }
      this.loadPayForms(this.lastFilter);
    });

    this.dataSource.sort = this.sort;
  }

  ngOnDestroy() {
    this.unsubscriber.complete();
  }

  sicBlur() {
    if ((this.currentFilterSic || '').toUpperCase() !== (this.sic || '').toUpperCase()) {
      this.employeeName = undefined;
    }
  }

  private lookupEmployee(value: string) {
    if (value) {
      const employee = value.toUpperCase();

      const request = new SearchEmployeeDetailsBySicRqst();
      request.includeZones = [this.includeZones];
      request.locationSic = this.sic;
      request.searchString = [employee];

      return this.hrService
        .searchEmployeeDetailsBySic(request, {
          loadingOverlayEnabled: false,
        })
        .pipe(
          catchError(() => {
            this.searching = false;
            return of(new SearchEmployeeDetailsBySicResp());
          })
        );
    }
  }

  refresh() {
    this.loadPayForms(this.lastFilter, true);
  }

  clearEmployeeName() {
    this.employeeName = undefined;
  }

  updateStatus(filter: PayformsFilterState) {
    if (filter.status && filter.status.length > 0) {
      this.statusSubmitted = filter.status.indexOf(PayformStatusCd.SUBMITTED) >= 0;
      this.statusApproved = filter.status.indexOf(PayformStatusCd.APPROVED) >= 0;
      this.statusReturned = filter.status.indexOf(PayformStatusCd.RETURNED) >= 0;
      this.statusNotSubmitted = filter.status.indexOf(PayformStatusCd.NEW) >= 0;
      this.statusDeleted = filter.status.indexOf(PayformStatusCd.DELETED) >= 0;
    } else {
      this.statusSubmitted = true;
      filter.status = [PayformStatusCd.SUBMITTED];
      this.store.dispatch(setPayformsFilterStateAction({ filter }));
    }

    this.statusAll = this.allOthersChecked();
    if (this.statusAll) {
      this.uncheckOthers();
    }
  }

  loadPayForms(filter: PayformsFilterState, bypassCache = false) {
    if (this.dataCache[this.paginator.pageIndex] && !bypassCache) {
      this.dataSource = this.dataCache[this.paginator.pageIndex];
      return;
    }

    if (!this.currentSort || this.currentSort.length === 0) {
      const sortField = new SortField();
      sortField.sortByFieldName = PayformSortFields.ShiftStartDate;
      sortField.sortOrder = 'asc';
      this.currentSort = [sortField];
    }

    const queryParams = new ListLinehaulPayformsQuery();
    queryParams.beginningShiftStartDate = filter.fromDate
      ? moment(filter.fromDate)
          .startOf('day')
          .format('YYYY-MM-DD')
      : undefined;
    queryParams.dmclSic = filter.sicCd;
    queryParams.dsrEmployeeId = this.substringPipe.transform(filter.employeeName, ['(', ')']);
    queryParams.endingShiftStartDate = filter.toDate ? moment(filter.toDate).format('YYYY-MM-DD') : undefined;
    queryParams.includeZone = this.includeZones ? 'true' : 'false';
    queryParams.numberOfRows = this.paginator.pageSize;
    queryParams.startAt = this.returnStartAt();
    queryParams.payformStatus = [...filter.status];
    if (_.includes(queryParams.payformStatus, PayformStatusCd.APPROVED)) {
      queryParams.payformStatus.push(PayformStatusCd.TRANSMITTED);
    }
    queryParams.sortByFieldName = [this.currentSort[0].sortByFieldName];
    queryParams.sortOrder = [this.currentSort[0].sortOrder];

    this.linehaulService
      .listLinehaulPayforms(queryParams)
      .pipe(take(1))
      .subscribe(
        response => {
          if (response && response.dsrPayform) {
            const data = response.dsrPayform.map(
              payform =>
                new ListPayformsCell(
                  payform.dsrPayformId,
                  _.replace(_.result(payform, 'dsrEmployeeDetail.fullName', ''), ',', ', '),
                  payform.dsrEmployeeId,
                  payform.dmclSic,
                  this.datePipe.transform(payform.shiftStartDate, 'MM/dd/yyyy'),
                  payform.statusCd === PayformStatusCd.NEW ? 'Not Submitted' : payform.statusCd === PayformStatusCd.TRANSMITTED ? PayformStatusCd.APPROVED : payform.statusCd
                )
            ) as ListPayformsCell[];
            const responseData = new MatTableDataSource(data);
            this.dataSource = responseData;
            this.dataCache[this.paginator.pageIndex] = responseData;
            this.paginator.length = response.listInfo.totalRowCount;
          } else {
            this.dataSource = new MatTableDataSource(undefined);
          }
        },
        error => {
          this.dataSource = new MatTableDataSource(undefined);
        }
      );
  }

  returnStartAt(): number {
    return this.paginator.pageIndex * this.paginator.pageSize;
  }

  sortData(sort: Sort) {
    this.dataCache = {};

    const newSort = new SortField();

    switch (sort.active) {
      case 'empName': {
        newSort.sortByFieldName = PayformSortFields.EmployeeNames;
        break;
      }
      case 'empId': {
        newSort.sortByFieldName = PayformSortFields.EmployeeID;
        break;
      }
      case 'sic': {
        newSort.sortByFieldName = PayformSortFields.SIC;
        break;
      }
      case 'shiftStartDate': {
        newSort.sortByFieldName = PayformSortFields.ShiftStartDate;
        break;
      }
      case 'status': {
        newSort.sortByFieldName = PayformSortFields.StatusCd;
        break;
      }
    }

    newSort.sortOrder = sort.direction;
    this.currentSort = [newSort];
    this.loadPayForms(this.lastFilter);
  }

  handleKeypress($event) {
    if ($event.key === 'Enter' && this.isDrawerOpen) {
      this.doneClicked();
    }
  }

  cancelClicked() {
    this.drawer.close();

    this.sic = this.currentFilterSic;
    this.employeeName = this.currentFilterEmployee;
    this.dateFrom = this.currentFilterFrom;
    this.dateTo = this.currentFilterTo;
    this.statusAll = this.currentFilterAllChecked;
    this.statusSubmitted = this.currentFilterSubmittedChecked;
    this.statusApproved = this.currentFilterApprovedChecked;
    this.statusReturned = this.currentFilterReturnedChecked;
    this.statusNotSubmitted = this.currentFilterNotSubmittedChecked;
    this.statusDeleted = this.currentFilterDeletedChecked;
  }

  doneClicked() {
    if (this.sicInput.errors || this.dateFromInput.errors || this.dateToInput.errors || this.employeeNameInput.errors) {
      return;
    }

    this.drawer.close();
    const autocompleteTrigger = this.employeeNameInput['valueAccessor'] as MatAutocompleteTrigger;
    autocompleteTrigger.closePanel();

    const filter = new PayformsFilter();
    filter.sicCd = this.sic ? this.sic.toUpperCase() : undefined;
    filter.fromDate = this.dateFrom ? moment(this.dateFrom) : undefined;
    filter.toDate = this.dateTo ? moment(this.dateTo) : undefined;
    filter.employeeName = this.employeeName;
    filter.status = this.getStatusFilter();

    if (
      filter.sicCd !== this.currentFilterSic ||
      filter.fromDate !== (this.currentFilterFrom ? moment(this.currentFilterFrom) : undefined) ||
      filter.toDate !== (this.currentFilterTo ? moment(this.currentFilterTo) : undefined) ||
      filter.employeeName !== this.currentFilterEmployee ||
      this.statusAll !== this.currentFilterAllChecked ||
      this.statusSubmitted !== this.currentFilterSubmittedChecked ||
      this.statusApproved !== this.currentFilterApprovedChecked ||
      this.statusReturned !== this.currentFilterReturnedChecked ||
      this.statusNotSubmitted !== this.currentFilterNotSubmittedChecked ||
      this.statusDeleted !== this.currentFilterDeletedChecked
    ) {
      this.paginator.pageIndex = 0;
      this.store.dispatch(setPayformsFilterStateAction({ filter }));
    }
  }

  saveCurrentFilterState() {
    this.currentFilterSic = this.sic;
    this.currentFilterEmployee = this.employeeName;
    this.currentFilterFrom = this.dateFrom;
    this.currentFilterTo = this.dateTo;
    this.currentFilterAllChecked = this.statusAll;
    this.currentFilterSubmittedChecked = this.statusSubmitted;
    this.currentFilterApprovedChecked = this.statusApproved;
    this.currentFilterReturnedChecked = this.statusReturned;
    this.currentFilterNotSubmittedChecked = this.statusNotSubmitted;
    this.currentFilterDeletedChecked = this.statusDeleted;
  }

  getStatusFilter(): PayformStatusCd[] {
    let statusList = [];

    if (this.statusAll) {
      statusList = [PayformStatusCd.APPROVED, PayformStatusCd.DELETED, PayformStatusCd.NEW, PayformStatusCd.RETURNED, PayformStatusCd.SUBMITTED];
    } else {
      if (this.statusSubmitted) {
        statusList.push(PayformStatusCd.SUBMITTED);
      }
      if (this.statusApproved) {
        statusList.push(PayformStatusCd.APPROVED);
      }
      if (this.statusReturned) {
        statusList.push(PayformStatusCd.RETURNED);
      }
      if (this.statusNotSubmitted) {
        statusList.push(PayformStatusCd.NEW);
      }
      if (this.statusDeleted) {
        statusList.push(PayformStatusCd.DELETED);
      }
    }

    return statusList;
  }

  statusChange(field: string, $event) {
    if (field === 'ALL') {
      setTimeout(() => {
        this.uncheckOthers();
        this.statusAll = true;
      });
    } else if (this.allOthersChecked()) {
      setTimeout(() => {
        this.uncheckOthers();
        this.statusAll = true;
      });
    } else if (this.anyOthersChecked()) {
      setTimeout(() => {
        this.statusAll = false;
      });
    }

    if (this.allUnchecked()) {
      setTimeout(() => {
        this.statusAll = true;
      });
    }
  }

  employeeNameBlur() {
    if (!this.employeeName) {
      this.searching = false;
    }
  }

  private allOthersChecked(): boolean {
    return this.statusApproved && this.statusDeleted && this.statusNotSubmitted && this.statusReturned && this.statusSubmitted;
  }

  private anyOthersChecked(): boolean {
    return this.statusApproved || this.statusDeleted || this.statusNotSubmitted || this.statusReturned || this.statusSubmitted;
  }

  private allUnchecked(): boolean {
    return !(this.statusAll || this.statusApproved || this.statusDeleted || this.statusNotSubmitted || this.statusReturned || this.statusSubmitted);
  }

  private uncheckOthers() {
    this.statusApproved = false;
    this.statusDeleted = false;
    this.statusNotSubmitted = false;
    this.statusReturned = false;
    this.statusSubmitted = false;
  }
}
